import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { FactureDashboardData, FactureDetails, IFACTURE, defaulValues, defaultValue } from '../../shared/model/facture.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from '../../shared/util/entity-utils';
import { UploadEntity } from '../scolarite/UploadFactureUpdater';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { getEleveInscriptions } from '../eleve/eleve.reducer';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<FactureDetails>,
  facturesByEleve: [] as ReadonlyArray<FactureDetails>,
  dashBoardEntities: defaulValues,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  frais: 0,
};

const apiUrl = 'api/factures';

export const getDashboardFactures = createAsyncThunk(
  'factures/fetch_facture_dashboard',
  async (scolariteId: string) => {
    const requestUrl = `${apiUrl}/dashboard/${scolariteId}`;
    const response = await axios.get<FactureDashboardData>(requestUrl);

    return response;
  },
  { serializeError: serializeAxiosError }
);

export const getFacturesByEleveId = createAsyncThunk(
  'factures/fetch_factures_by_eleve_id',
  async (entity: any) => {
    const requestUrl = `${apiUrl}/eleve/${entity.eleveId}?anneeScolaireId=${entity.anneeScolaireId}`;
    const response = await axios.get<FactureDetails[]>(requestUrl);
    return { factureWithScolariteDetails: response.data };
  },
  { serializeError: serializeAxiosError }
);

export const getFactures = createAsyncThunk('factures/fetch_facture_list', async (entity: IFACTURE) => {
  let requestUrl = '';
  let statutFacture;
  let searchUrl = '';
  if (entity.statutFacture) {
    statutFacture = '&statutFacture=' + entity.statutFacture;
    searchUrl += statutFacture;
  }

  if (!entity.statutFacture) {
    requestUrl = `${apiUrl}/scolarite/${entity.id}?page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}/scolarite/${entity.id}?page=${entity.page}&size=${entity.size}${searchUrl ? '&' : '?'}${searchUrl}`;
  }
  const response = await axios.get<FactureDetails[]>(requestUrl);
  const totalItems = parseInt(response.headers['x-total-count'], 10) || 0;

  if (totalItems === 0) {
    return { factureWithEleveDetails: [], totalItems };
  }

  return { factureWithEleveDetails: response.data, totalItems };
});

export const getFacture = createAsyncThunk(
  'facture/facture',
  async (factureId: string | number) => {
    const requestUrl = `${apiUrl}/${factureId}`;
    const response = await axios.get<FactureDetails>(requestUrl);

    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getFrais = createAsyncThunk(
  'facture/facture-frais',
  async (factureId: string | number) => {
    const requestUrl = `${apiUrl}/${factureId}/frais`;
    const response = await axios.get<number>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const genererFactureInscription = createAsyncThunk(
  'factures/generer_facture_inscription',
  async (entity: IFACTURE, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.inscriptionId}/generate`;
    const response = await axios.post<IFACTURE[]>(requestUrl);
    thunkAPI.dispatch(getEleveInscriptions(entity.eleveId));
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const sendFacturesToParents = createAsyncThunk('factures/send_facture_to_parents', async (entity: IFACTURE, thunkAPI) => {
  const requestUrl = `${apiUrl}/send/${entity.scolariteId}`;
  const response = await axios.post<IFACTURE[]>(requestUrl);
  const getFactureEntity = {
    ecoleId: entity.ecoleId,
    id: entity.scolariteId,
    page: 0,
    size: ITEMS_PER_PAGE,
  };
  thunkAPI.dispatch(getFactures(getFactureEntity));
  return response;
});

export const downloadFactureByScolariteID = createAsyncThunk('factures/download_facture_scolarite', async (urlUpload: string | number) => {
  const requestUrl = `${apiUrl}${urlUpload}`;
  const response = await axios.get(requestUrl, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `facture_scolarite.xlsx`);
  document.body.appendChild(link);
  link.click();

  return response.data;
});

export const resendFacture = createAsyncThunk('factures/resend_facture_to_parent', async (factureId: string | number, thunkAPI) => {
  const requestUrl = `${apiUrl}/resend/facture/${factureId}`;
  const response = await axios.post<IFACTURE[]>(requestUrl);
  thunkAPI.dispatch(getFacture(factureId));
  return response;
});

export const solderFacture = createAsyncThunk(
  'facture/solderFacture',
  async (factureId: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${factureId}/solder`;
    const response = await axios.post<IFACTURE>(requestUrl);
    thunkAPI.dispatch(getFacture(factureId));
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const solderFactureByPartenaire = createAsyncThunk(
  'facture/solderFacturePartner',
  async ({ factureId, factureData }: { factureId: string | number; factureData: any }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${factureId}/pay`;
    const response = await axios.put<IFACTURE>(requestUrl, factureData);
    thunkAPI.dispatch(getFacture(factureId));
    return response;
  },
  { serializeError: serializeAxiosError }
);

export const uploadFactureEleveList = createAsyncThunk(
  'eleve/init_upload',
  async (entity: UploadEntity, thunkAPI) => {
    const result = await axios.post(
      `${apiUrl}/init-upload-eleves?ecoleId=${entity.ecoleId}&scolariteId=${entity.scolariteId}`,
      entity.file,
      {
        headers: { 'content-type': 'multipart/form-data' },
      }
    );
    const getFactureEntity = {
      ecoleId: entity.ecoleId,
      id: entity.scolariteId,
      page: 0,
      size: ITEMS_PER_PAGE,
    };
    thunkAPI.dispatch(getFactures(getFactureEntity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateFacture = createAsyncThunk(
  'facture/update-facture',
  async (entity: FactureDetails, thunkAPI) => {
    if (entity.factureId === undefined) {
      throw new Error('ID is undefined');
    }
    const result = await axios.put<FactureDetails>(`${apiUrl}/${entity.factureId}`, cleanEntity(entity));
    thunkAPI.dispatch(getFacture(entity.factureId));
    thunkAPI.dispatch(getFrais(entity.factureId));
    const getFactureEntity = {
      ecoleId: entity.ecoleId,
      id: entity.scolariteId,
      page: 0,
      size: ITEMS_PER_PAGE,
    };
    thunkAPI.dispatch(getFactures(getFactureEntity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// Création du slice Redux
export type FactureState = Readonly<typeof initialState>;
const FactureSlice = createSlice({
  name: 'facture',
  initialState: initialState as FactureState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getFactures), (state, action) => {
        state.loading = false;
        state.entities = action.payload.factureWithEleveDetails;
        state.totalItems = action.payload.totalItems;
      })
      .addMatcher(isFulfilled(getFacture), (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })
      .addMatcher(isFulfilled(partialUpdateFacture), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getFrais), (state, action) => {
        state.loading = false;
        state.frais = action.payload;
      })
      .addMatcher(isFulfilled(getFacturesByEleveId), (state, action) => {
        state.loading = false;
        state.facturesByEleve = action.payload.factureWithScolariteDetails;
      })
      .addMatcher(isFulfilled(getDashboardFactures), (state, action) => {
        state.loading = false;
        state.dashBoardEntities = action.payload.data;
      })
      .addMatcher(isFulfilled(sendFacturesToParents), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(isFulfilled(genererFactureInscription), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(isFulfilled(resendFacture), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(isFulfilled(solderFacture), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(isFulfilled(solderFactureByPartenaire), (state, action) => {
        state.updating = false;
        state.loading = false;
      })
      .addMatcher(
        isRejected(getFactures, getFacture, getFacturesByEleveId, getFrais, partialUpdateFacture, getDashboardFactures),
        (state, action) => {
          state.loading = false;
        }
      )
      .addMatcher(isPending(getFactures, getFacture, getFacturesByEleveId, getFrais), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(getDashboardFactures), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(sendFacturesToParents, resendFacture, genererFactureInscription), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(solderFacture), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(solderFactureByPartenaire), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(downloadFactureByScolariteID), state => {
        state.errorMessage = null;
        state.loading = false;
      });
  },
});

export const { reset } = FactureSlice.actions;

export default FactureSlice.reducer;
